import {
  useEffect,
  React,
  useCallback,
  createContext,
  useContext,
  useState,
  useMemo,
  useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { myToaster } from '@interstellar-component'
import { get, post, getCookie } from './services/NetworkUtils'

const AppService = {
  getSession: async () => await get('/v1/auth/session'),
  logout: async (data) => await post('/v1/auth/logout', data),
}

const AppContext = createContext()

function AppProvider({ children }) {
  const [slider, setSlider] = useState(false)
  const [cookies, , removeCookie] = useCookies(['token-backoffice'])
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  // Session inactivity timeout in minutes, sourced from /auth/session (the
  // "Session Auto Timeout" general setting). null → idle-logout disabled.
  const [sessionTimeout, setSessionTimeout] = useState(null)
  // Holding-scoped company summary from /auth/session ({ count, companies }).
  // Drives the /company single-vs-table switch and the draft-completion banner.
  const [companyContext, setCompanyContext] = useState(null)
  // Whether the LAST /auth/session attempt failed. `companyContext === null` on
  // its own cannot express this: it is also the initial value, and the value a
  // successful response carries when the BE omits company_context. Consumers
  // that branch on the company count need the difference, because "the session
  // never answered" and "the session says you have no companies" look identical
  // from the context alone and lead to opposite UI.
  const [sessionError, setSessionError] = useState(false)
  const location = useLocation()
  const lastActivityRef = useRef(Date.now())
  // Read pathname via ref so getSession's identity is stable across navigations.
  // Previously [location.pathname] was a useCallback dep, which made getSession a
  // brand-new function on every nav and re-fired the mounting effect each click.
  const pathnameRef = useRef(location.pathname)
  pathnameRef.current = location.pathname

  const getSession = useCallback(
    () =>
      AppService.getSession()
        .then((res) => {
          const userData = res?.data?.user
          setUser({
            ...userData,
            photo_url: userData?.avatar_url
              ? `${userData.avatar_url}?time=${new Date().getTime()}`
              : null,
          })
          setPermissions(res?.data?.permissions ?? [])
          setSessionTimeout(res?.data?.session_timeout ?? null)
          setCompanyContext(res?.data?.company_context ?? null)
          setSessionError(false)
          setPermissionsLoaded(true)
        })
        .catch((err) => {
          // Flag it as well as toasting it. A toast is gone in seconds and
          // never reaches the components that have to decide what to render;
          // /company in particular used to read the resulting empty context as
          // "this holding has no companies" and drop single-company users into
          // an empty table.
          setSessionError(true)
          setPermissionsLoaded(true)
          if (pathnameRef.current !== '/login') {
            myToaster(err)
          }
        }),
    []
  )

  const hasPermission = useCallback(
    (moduleKey, subPermission = null) => {
      const perm = permissions.find((p) => p.module_key === moduleKey)
      if (!perm) return false
      if (subPermission === null) return true
      return perm.sub_permissions.includes(subPermission)
    },
    [permissions]
  )

  const logoutFunction = useCallback((refreshToken) => {
    AppService.logout({ refresh_token: refreshToken })
      .then(() => {})
      .catch((err) => {
        console.warn(err)
      })
  }, [])

  //   const getAccess = useCallback(
  //     (accesName) => accesses?.find((acc) => acc.name === accesName),
  //     [accesses]
  //   )

  const logout = useCallback(() => {
    const refreshToken = getCookie('refresh-token-backoffice')
    logoutFunction(refreshToken)
    localStorage.removeItem('user_id')
    localStorage.removeItem('RrwF57&aRMoR5Eq23#Mi') // user_id
    localStorage.removeItem('email_forget_password')
    localStorage.removeItem('countdown_to_new_otp')
    // Drop the previously-selected dashboard company so the next user does not
    // inherit it (the guard would toast "Company not accessible to current
    // user" while fetching the old tenant under the new token — Everest #86exz9njd).
    localStorage.removeItem('everest:dashboard:company_id')
    removeCookie('token-backoffice', { path: '/' })
    removeCookie('refresh-token-backoffice', { path: '/' })
    // Reset in-memory session state too. Without this, after Sign out the
    // provider still holds the previous user's `user`/`permissions` and
    // `permissionsLoaded` stays true, so on the next login LandingRedirect skips
    // the spinner and mounts the dashboard against the OLD identity while the new
    // getSession() is still in flight — firing a cross-tenant fetch → 403 toast.
    // Clearing permissionsLoaded forces the spinner until the new session loads.
    setUser(null)
    setPermissions([])
    setPermissionsLoaded(false)
    setCompanyContext(null)
    setSessionError(false)
  }, [logoutFunction, removeCookie])

  useEffect(() => {
    if (location.pathname !== '/login' && cookies['token-backoffice']) {
      getSession()
    }
    // getSession is stable (deps: []); we only want to refetch when the auth cookie
    // appears/disappears or the route enters/leaves /login — not on every tab click.
  }, [cookies, location.pathname, getSession])

  // Re-check the session (→ company_context) when the user returns to the tab —
  // e.g. after signing the DocuSign email in another tab — so the /company
  // single-view + draft banner reflect the latest status without a manual reload.
  useEffect(() => {
    if (!cookies['token-backoffice']) return undefined
    const refresh = () => {
      if (document.visibilityState === 'visible' && pathnameRef.current !== '/login') {
        getSession()
      }
    }
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [cookies, getSession])

  // ─── Idle / Session Auto Timeout ───────────────────────────────────────────
  // Sign the user out after `sessionTimeout` minutes of no activity. We compare
  // wall-clock elapsed time against a polling interval (rather than a single
  // setTimeout) so a sleeping/throttled tab can't silently keep the session
  // alive past the timeout — on wake the next poll sees the gap and logs out.
  useEffect(() => {
    const ttlMinutes = Number(sessionTimeout)
    if (!ttlMinutes || ttlMinutes <= 0) return undefined
    if (!cookies['token-backoffice']) return undefined

    const ttlMs = ttlMinutes * 60 * 1000
    lastActivityRef.current = Date.now()

    // Throttle activity bookkeeping so high-frequency events (mousemove/scroll)
    // don't write on every tick.
    let throttled = false
    const markActivity = () => {
      if (throttled) return
      throttled = true
      lastActivityRef.current = Date.now()
      setTimeout(() => {
        throttled = false
      }, 1000)
    }

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']
    events.forEach((evt) => window.addEventListener(evt, markActivity))

    const pollMs = Math.min(30000, Math.max(5000, ttlMs / 4))
    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= ttlMs) {
        clearInterval(interval)
        events.forEach((evt) => window.removeEventListener(evt, markActivity))
        logout()
        myToaster({
          message: 'You have been signed out due to inactivity.',
          type: 'info',
        })
      }
    }, pollMs)

    return () => {
      clearInterval(interval)
      events.forEach((evt) => window.removeEventListener(evt, markActivity))
    }
  }, [sessionTimeout, cookies, logout])

  const contextValue = useMemo(
    () => ({
      user,
      permissions,
      permissionsLoaded,
      hasPermission,
      companyContext,
      sessionError,
      slider,
      setSlider,
      getSession,
      logout,
    }),
    [
      user,
      permissions,
      permissionsLoaded,
      hasPermission,
      companyContext,
      sessionError,
      slider,
      getSession,
      logout,
    ]
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}

const useApp = () => {
  const context = useContext(AppContext)
  if (!context) throw new Error('useApp must be used within a AppProvider')
  return context
}

export { AppProvider, useApp }
